// Ingressa - Cliente API
import { friendlyNetworkError } from '../utils/friendlyErrors';

const API_BASE = '/api';

function postMultipartUpload(formData) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API_BASE}/documents/upload`);
    xhr.withCredentials = true;
    xhr.onload = () => {
      let data = {};
      try {
        data = JSON.parse(xhr.responseText || '{}');
      } catch {
        /* resposta não-JSON */
      }
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(data);
        return;
      }
      reject(new Error(data.error || `Erro no upload (${xhr.status})`));
    };
    xhr.onerror = () => reject(new Error('Failed to fetch'));
    xhr.send(formData);
  });
}

async function fetchApi(path, options = {}) {
  const isGet = (options.method || 'GET').toUpperCase() === 'GET';
  const url = isGet
    ? `${API_BASE}${path}${path.includes('?') ? '&' : '?'}_=${Date.now()}`
    : `${API_BASE}${path}`;
  const res = await fetch(url, {
    ...options,
    credentials: 'include',
    cache: 'no-store',
    headers: {
      'Content-Type': 'application/json',
      'Cache-Control': 'no-cache, no-store, must-revalidate',
      'Pragma': 'no-cache',
      ...options.headers,
    },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data.error || 'Erro na requisição');
    err.status = res.status;
    err.reason = data.reason || null;
    throw err;
  }
  return data;
}

export const apiClient = {
  // Master (proprietário)
  getPlatformAbout: () => fetchApi('/platform/about'),
  updateMasterPlatformAbout: (data) =>
    fetchApi('/master/platform-about', { method: 'PUT', body: JSON.stringify(data) }),
  getMasterOwnerInstitution: () => fetchApi('/master/owner-institution'),
  updateMasterOwnerInstitution: (data) =>
    fetchApi('/master/owner-institution', { method: 'PUT', body: JSON.stringify(data) }),
  getMasterInstitutions: () => fetchApi('/master/institutions'),
  getMasterSelectionModes: () => fetchApi('/master/selection-modes'),
  createMasterInstitution: (data) => fetchApi('/master/institutions', { method: 'POST', body: JSON.stringify(data) }),
  updateMasterInstitution: (id, data) => fetchApi(`/master/institutions/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  getMasterInstitutionUsers: (instId) => fetchApi(`/master/institutions/${instId}/users`),
  createMasterInstitutionUser: (instId, data) => fetchApi(`/master/institutions/${instId}/users`, { method: 'POST', body: JSON.stringify(data) }),
  updateMasterInstitutionUser: (instId, userId, data) => fetchApi(`/master/institutions/${instId}/users/${userId}`, { method: 'PUT', body: JSON.stringify(data) }),
  getMasterExportPreview: (instId) => fetchApi(`/master/institutions/${instId}/export/preview`),
  exportMasterInstitution: async (instId, slug = '') => {
    const url = `${typeof window !== 'undefined' ? window.location.origin : ''}${API_BASE}/master/institutions/${instId}/export`;
    const res = await fetch(url, { method: 'POST', credentials: 'include' });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || 'Erro ao exportar');
    }
    const blob = await res.blob();
    let filename = res.headers.get('X-Export-Filename');
    if (!filename) {
      const cd = res.headers.get('Content-Disposition') || '';
      filename = cd.match(/filename[*]?=(?:UTF-8'')?["]?([^";\n]+)/i)?.[1]?.trim() || `export_${slug || instId}.zip`;
    }
    return { blob, filename };
  },
  finalizeMasterContract: (instId, confirmSlug) => fetchApi(`/master/institutions/${instId}/finalize_contract`, {
    method: 'POST',
    body: JSON.stringify({ confirm_slug: confirmSlug }),
  }),
  getMyScheduleAcks: () => fetchApi('/me/schedule-acks'),
  acknowledgeScheduleChanges: (processIds) =>
    fetchApi('/me/schedule-acks', {
      method: 'POST',
      body: JSON.stringify({ process_ids: processIds }),
    }),
  getPushConfig: () => fetchApi('/push/config'),
  getPushStatus: () => fetchApi('/push/status'),
  subscribePush: (subscription) =>
    fetchApi('/push/subscribe', { method: 'POST', body: JSON.stringify({ subscription }) }),
  unsubscribePush: (endpoint) =>
    fetchApi('/push/unsubscribe', { method: 'POST', body: JSON.stringify({ endpoint }) }),
  getConfig: () => fetchApi('/config'),
  getConfigMail: () => fetchApi('/config/mail'),
  getConfigCadunico: () => fetchApi('/config/cadunico'),
  updateConfig: (data) => fetchApi('/config', { method: 'PUT', body: JSON.stringify(data) }),
  generateBackup: async () => {
    const data = await fetchApi('/backup/generate', { method: 'POST' });
    return data;
  },
  getBackupList: () => fetchApi('/backup/list'),
  getBackupConfig: () => fetchApi('/backup/config'),
  updateBackupConfig: (data) => fetchApi('/backup/config', { method: 'PUT', body: JSON.stringify(data) }),
  downloadBackup: (filename) => `/api/backup/download/${encodeURIComponent(filename)}`,
  restoreBackup: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const res = await fetch('/api/backup/restore', {
      method: 'POST',
      credentials: 'include',
      body: formData,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || 'Erro ao restaurar');
    return data;
  },
  uploadIcon: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const res = await fetch('/api/config/upload-icon', {
      method: 'POST',
      credentials: 'include',
      body: formData,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || 'Erro no upload');
    return data.url;
  },
  uploadDocument: async (file, docType) => {
    const formData = new FormData();
    formData.append('file', file);
    if (docType) formData.append('doc_type', docType);
    let res;
    try {
      res = await fetch('/api/documents/upload', {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });
    } catch (err) {
      throw new Error(friendlyNetworkError(err?.message || 'Erro no upload'));
    }
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || 'Erro no upload');
    return { url: data.url, name: data.name || file.name };
  },
  downloadDocument: async (opts) => {
    const params = new URLSearchParams();
    if (opts.appealId) params.set('appeal_id', opts.appealId);
    if (opts.exemptionRequestId) params.set('exemption_request_id', opts.exemptionRequestId);
    if (opts.specialAttendanceRequestId) params.set('special_attendance_request_id', opts.specialAttendanceRequestId);
    if (opts.applicationId) {
      params.set('application_id', opts.applicationId);
      if (opts.type) params.set('type', opts.type);
    }
    params.set('index', opts.index ?? 0);
    const res = await fetch(`/api/documents/download?${params}`, { credentials: 'include' });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.error || 'Erro ao baixar');
    }
    return res.blob();
  },
  auth: {
    signIn: async ({ email, password }) => {
      try {
        const { user } = await fetchApi('/login', {
          method: 'POST',
          body: JSON.stringify({ email, password }),
        });
        return { data: { user }, error: null };
      } catch (err) {
        return { data: null, error: err };
      }
    },
    signOut: async () => {
      try {
        await fetchApi('/logout', { method: 'POST' });
        return { error: null };
      } catch {
        return { error: null };
      }
    },
    getSession: async () => {
      try {
        const { user } = await fetchApi('/me');
        return { data: { session: { user } }, error: null };
      } catch (err) {
        return { data: null, error: err };
      }
    },
    changePassword: async ({ current_password, password }) => {
      const { user } = await fetchApi('/change-password', {
        method: 'POST',
        body: JSON.stringify({ current_password, password }),
      });
      return user;
    },
    updateMe: async (data) => {
      try {
        const { user } = await fetchApi('/me', { method: 'PATCH', body: JSON.stringify(data) });
        return { data: { user }, error: null };
      } catch (err) {
        return { data: null, error: err.message || 'Erro ao atualizar' };
      }
    },
    forgotPassword: async (email) => {
      try {
        await fetchApi('/forgot-password', {
          method: 'POST',
          body: JSON.stringify({ email }),
        });
        return { error: null };
      } catch (err) {
        return { error: err.message };
      }
    },
    resetPassword: async ({ token, password }) => {
      const res = await fetch('/api/reset-password', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Erro ao redefinir senha');
      return data;
    },
  },
  getProcesses: (all = false, forAnalyst = false) => {
    const params = new URLSearchParams();
    if (all) params.set('all', '1');
    if (forAnalyst) params.set('for_analyst', '1');
    return fetchApi(`/processes${params.toString() ? '?' + params.toString() : ''}`);
  },
  getProcessQuestionnaire: (processId) => fetchApi(`/processes/${processId}/questionnaire`),
  getProcessDocuments: (processId) => fetchApi(`/processes/${processId}/documents`),
  getProcessSchedule: (processId) => fetchApi(`/processes/${processId}/schedule`),
  createProcessScheduleItem: (processId, data) => fetchApi(`/processes/${processId}/schedule`, { method: 'POST', body: JSON.stringify(data) }),
  updateProcessScheduleItem: (id, data) => fetchApi(`/process_schedule_items/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  updateProcessScheduleSystemItem: (processId, data) =>
    fetchApi(`/processes/${processId}/schedule/system-item`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteProcessScheduleItem: (id) => fetchApi(`/process_schedule_items/${id}`, { method: 'DELETE' }),
  createProcessDocument: async (processId, formData) => {
    const res = await fetch(`${API_BASE}/processes/${processId}/documents`, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || 'Erro ao enviar');
    return data;
  },
  updateProcessDocument: (id, data) => fetchApi(`/process_documents/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteProcessDocument: (id) => fetchApi(`/process_documents/${id}`, { method: 'DELETE' }),
  downloadProcessDocument: async (id) => {
    const res = await fetch(`${API_BASE}/process_documents/${id}/download`, { credentials: 'include' });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.error || 'Erro ao baixar');
    }
    return res.blob();
  },
  getCourses: (processId) => fetchApi(`/courses${processId ? `?process_id=${processId}` : ''}`),
  compareCourseMatrices: (courseId1, courseId2) =>
    fetchApi(`/courses/compare_matrices?course_id_1=${courseId1}&course_id_2=${courseId2}`),
  getControlPanel: (processId) => fetchApi(`/control_panel${processId ? `?process_id=${processId}` : ''}`),
  getApplications: (opts) => {
    const params = new URLSearchParams();
    if (opts?.forCurricular) params.set('for_curricular', '1');
    if (opts?.forExamGrades) params.set('for_exam_grades', '1');
    if (opts?.forExamObjective) params.set('for_exam_objective', '1');
    if (opts?.forExamEssay) params.set('for_exam_essay', '1');
    if (opts?.processId) params.set('process_id', opts.processId);
    return fetchApi(`/applications${params.toString() ? '?' + params.toString() : ''}`);
  },
  assignExamGrader: async (applicationId, examGradeAnalystId) => {
    try {
      const res = await fetchApi(`/applications/${applicationId}/assign_exam_grader`, {
        method: 'PUT',
        body: JSON.stringify({ exam_grade_analyst_id: examGradeAnalystId }),
      });
      return { data: res, error: null };
    } catch (err) {
      return { data: null, error: err.message };
    }
  },
  bulkAssignExamGrader: async (applicationIds, analystId) => {
    try {
      const res = await fetchApi('/applications/bulk_assign_exam_grader', {
        method: 'POST',
        body: JSON.stringify({ application_ids: applicationIds, analyst_id: analystId }),
      });
      return { data: res, error: null };
    } catch (err) {
      return { data: null, error: err.message };
    }
  },
  bulkAssignExamGraderEquitable: async (applicationIds, analystIds) => {
    try {
      const res = await fetchApi('/applications/bulk_assign_exam_grader_equitable', {
        method: 'POST',
        body: JSON.stringify({ application_ids: applicationIds, analyst_ids: analystIds }),
      });
      return { data: res, error: null };
    } catch (err) {
      return { data: null, error: err.message };
    }
  },
  assignExamEssayGrader: async (applicationId, examGradeAnalystId) => {
    try {
      const res = await fetchApi(`/applications/${applicationId}/assign_exam_essay_grader`, {
        method: 'PUT',
        body: JSON.stringify({ exam_grade_analyst_id: examGradeAnalystId }),
      });
      return { data: res, error: null };
    } catch (err) {
      return { data: null, error: err.message };
    }
  },
  bulkAssignExamEssayGrader: async (applicationIds, analystId) => {
    try {
      const res = await fetchApi('/applications/bulk_assign_exam_essay_grader', {
        method: 'POST',
        body: JSON.stringify({ application_ids: applicationIds, analyst_id: analystId }),
      });
      return { data: res, error: null };
    } catch (err) {
      return { data: null, error: err.message };
    }
  },
  bulkAssignExamEssayGraderEquitable: async (applicationIds, analystIds) => {
    try {
      const res = await fetchApi('/applications/bulk_assign_exam_essay_grader_equitable', {
        method: 'POST',
        body: JSON.stringify({ application_ids: applicationIds, analyst_ids: analystIds }),
      });
      return { data: res, error: null };
    } catch (err) {
      return { data: null, error: err.message };
    }
  },
  acceptExamObjectiveAssignment: async (applicationId) => {
    try {
      const res = await fetchApi(`/applications/${applicationId}/accept_exam_objective_assignment`, { method: 'POST' });
      return { data: res, error: null };
    } catch (err) {
      return { data: null, error: err.message };
    }
  },
  returnExamObjectiveAssignment: async (applicationId, reason) => {
    try {
      const res = await fetchApi(`/applications/${applicationId}/return_exam_objective_assignment`, {
        method: 'POST',
        body: JSON.stringify({ reason }),
      });
      return { data: res, error: null };
    } catch (err) {
      return { data: null, error: err.message };
    }
  },
  acceptExamEssayAssignment: async (applicationId) => {
    try {
      const res = await fetchApi(`/applications/${applicationId}/accept_exam_essay_assignment`, { method: 'POST' });
      return { data: res, error: null };
    } catch (err) {
      return { data: null, error: err.message };
    }
  },
  returnExamEssayAssignment: async (applicationId, reason) => {
    try {
      const res = await fetchApi(`/applications/${applicationId}/return_exam_essay_assignment`, {
        method: 'POST',
        body: JSON.stringify({ reason }),
      });
      return { data: res, error: null };
    } catch (err) {
      return { data: null, error: err.message };
    }
  },
  uploadExamObjectiveScan: async (applicationId, file) => {
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`/api/applications/${applicationId}/exam_objective_scan`, {
        method: 'POST',
        body: fd,
        credentials: 'include',
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Erro no envio da folha');
      return { data, error: null };
    } catch (err) {
      return { data: null, error: err.message };
    }
  },
  deleteExamObjectiveScan: async (applicationId) => {
    try {
      const res = await fetchApi(`/applications/${applicationId}/exam_objective_scan`, { method: 'DELETE' });
      return { data: res, error: null };
    } catch (err) {
      return { data: null, error: err.message };
    }
  },
  batchUploadExamObjectiveScans: async (processId, files) => {
    try {
      const fd = new FormData();
      (files || []).forEach((f) => fd.append('files', f));
      const res = await fetch(`/api/processes/${processId}/exam_objective_scans/batch`, {
        method: 'POST',
        body: fd,
        credentials: 'include',
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Erro no envio em lote');
      return { data, error: null };
    } catch (err) {
      return { data: null, error: err.message };
    }
  },
  getExamObjectiveScansPending: (processId) =>
    fetchApi(`/processes/${processId}/exam_objective_scans/pending`),
  linkExamObjectiveScanPending: async (processId, pendingId, applicationId) => {
    try {
      const res = await fetchApi(`/processes/${processId}/exam_objective_scans/pending/${pendingId}/link`, {
        method: 'PUT',
        body: JSON.stringify({ application_id: applicationId }),
      });
      return { data: res, error: null };
    } catch (err) {
      return { data: null, error: err.message };
    }
  },
  discardExamObjectiveScanPending: async (processId, pendingId) => {
    try {
      const res = await fetchApi(`/processes/${processId}/exam_objective_scans/pending/${pendingId}`, {
        method: 'DELETE',
      });
      return { data: res, error: null };
    } catch (err) {
      return { data: null, error: err.message };
    }
  },
  runExamObjectiveOmrBatch: async (processId) => {
    try {
      const res = await fetchApi(`/processes/${processId}/exam_objective_scans/run_omr`, { method: 'POST' });
      return { data: res, error: null };
    } catch (err) {
      return { data: null, error: err.message };
    }
  },
  uploadExamEssayScan: async (applicationId, file) => {
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`/api/applications/${applicationId}/exam_essay_scan`, {
        method: 'POST',
        body: fd,
        credentials: 'include',
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Erro no envio da folha');
      return { data, error: null };
    } catch (err) {
      return { data: null, error: err.message };
    }
  },
  deleteExamEssayScan: async (applicationId) => {
    try {
      const res = await fetchApi(`/applications/${applicationId}/exam_essay_scan`, { method: 'DELETE' });
      return { data: res, error: null };
    } catch (err) {
      return { data: null, error: err.message };
    }
  },
  batchUploadExamEssayScans: async (processId, files) => {
    try {
      const fd = new FormData();
      (files || []).forEach((f) => fd.append('files', f));
      const res = await fetch(`/api/processes/${processId}/exam_essay_scans/batch`, {
        method: 'POST',
        body: fd,
        credentials: 'include',
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Erro no envio em lote');
      return { data, error: null };
    } catch (err) {
      return { data: null, error: err.message };
    }
  },
  getExamEssayScansPending: (processId) => fetchApi(`/processes/${processId}/exam_essay_scans/pending`),
  linkExamEssayScanPending: async (processId, pendingId, applicationId) => {
    try {
      const res = await fetchApi(`/processes/${processId}/exam_essay_scans/pending/${pendingId}/link`, {
        method: 'PUT',
        body: JSON.stringify({ application_id: applicationId }),
      });
      return { data: res, error: null };
    } catch (err) {
      return { data: null, error: err.message };
    }
  },
  discardExamEssayScanPending: async (processId, pendingId) => {
    try {
      const res = await fetchApi(`/processes/${processId}/exam_essay_scans/pending/${pendingId}`, {
        method: 'DELETE',
      });
      return { data: res, error: null };
    } catch (err) {
      return { data: null, error: err.message };
    }
  },
  getExamObjectiveReview: async (applicationId) => {
    try {
      const res = await fetchApi(`/applications/${applicationId}/exam_objective_review`);
      return { data: res, error: null };
    } catch (err) {
      return { data: null, error: err.message };
    }
  },
  getProcessExamOmrCalibration: async (processId) => {
    try {
      const res = await fetchApi(`/processes/${processId}/exam_omr_calibration`);
      return { data: res, error: null };
    } catch (err) {
      return { data: null, error: err.message };
    }
  },
  putProcessExamOmrCalibration: async (processId, body) => {
    try {
      const res = await fetchApi(`/processes/${processId}/exam_omr_calibration`, {
        method: 'PUT',
        body: JSON.stringify(body || {}),
      });
      return { data: res, error: null };
    } catch (err) {
      return { data: null, error: err.message };
    }
  },
  uploadProcessExamOmrCalibrationReference: async (processId, file) => {
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`/api/processes/${processId}/exam_omr_calibration/reference`, {
        method: 'POST',
        body: fd,
        credentials: 'include',
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Erro ao enviar folha modelo');
      return { data, error: null };
    } catch (err) {
      return { data: null, error: err.message };
    }
  },
  discardProcessExamOmrCalibrationStaging: async (processId) => {
    try {
      const res = await fetchApi(`/processes/${processId}/exam_omr_calibration/staging`, {
        method: 'DELETE',
      });
      return { data: res, error: null };
    } catch (err) {
      return { data: null, error: err.message };
    }
  },
  getProcessExamOmrCalibrationReview: async (processId) => {
    try {
      const res = await fetchApi(`/processes/${processId}/exam_omr_calibration/review`);
      return { data: res, error: null };
    } catch (err) {
      return { data: null, error: err.message };
    }
  },
  putExamGrades: async (applicationId, examAreaScores) => {
    try {
      const res = await fetchApi(`/applications/${applicationId}/exam_grades`, {
        method: 'PUT',
        body: JSON.stringify({ exam_area_scores: examAreaScores }),
      });
      return { data: res, error: null };
    } catch (err) {
      return { data: null, error: err.message };
    }
  },
  putExamEssayGrades: async (applicationId, payload) => {
    try {
      const res = await fetchApi(`/applications/${applicationId}/exam_essay_grades`, {
        method: 'PUT',
        body: JSON.stringify(payload || {}),
      });
      return { data: res, error: null };
    } catch (err) {
      return { data: null, error: err.message };
    }
  },
  getApplication: (id) => fetchApi(`/applications/${id}`),
  getAnalysisHistory: (applicationId) => fetchApi(`/applications/${applicationId}/analysis_history`),

  createApplication: async (data) => {
    try {
      const res = await fetchApi('/applications', { method: 'POST', body: JSON.stringify(data) });
      return { data: res, error: null };
    } catch (err) {
      return { data: null, error: err.message };
    }
  },
  updateApplication: async (id, data) => {
    try {
      const res = await fetchApi(`/applications/${id}`, { method: 'PUT', body: JSON.stringify(data) });
      return { data: res, error: null };
    } catch (err) {
      return { data: null, error: err.message };
    }
  },
  updateApplicationQuota: async (applicationId, quotaType) => {
    try {
      const res = await fetchApi(`/applications/${applicationId}/quota`, {
        method: 'PUT',
        body: JSON.stringify({ quota_type: quotaType })
      });
      return { data: res, error: null };
    } catch (err) {
      return { data: null, error: err.message };
    }
  },
  acceptCurricularAnalysis: async (applicationId) => {
    try {
      const res = await fetchApi(`/applications/${applicationId}/accept_curricular_analysis`, { method: 'POST' });
      return { data: res, error: null };
    } catch (err) {
      return { data: null, error: err.message };
    }
  },
  returnCurricularAnalysis: async (applicationId, reason) => {
    try {
      const res = await fetchApi(`/applications/${applicationId}/return_curricular_analysis`, {
        method: 'POST',
        body: JSON.stringify({ reason: reason || '' })
      });
      return { data: res, error: null };
    } catch (err) {
      return { data: null, error: err.message };
    }
  },
  consultCadunico: (applicationId, data = {}) => fetchApi(`/applications/${applicationId}/cadunico_consult`, { method: 'POST', body: JSON.stringify(data || {}) }),
  resendExemption: async (applicationId, documents, extra = {}) => {
    try {
      const res = await fetchApi(`/applications/${applicationId}/resend_exemption`, {
        method: 'POST',
        body: JSON.stringify({ documents, ...(extra || {}) })
      });
      return { data: res, error: null };
    } catch (err) {
      return { data: null, error: err.message };
    }
  },
  resendCurricular: async (applicationId, documents) => {
    try {
      const res = await fetchApi(`/applications/${applicationId}/resend_curricular`, {
        method: 'POST',
        body: JSON.stringify({ documents })
      });
      return { data: res, error: null };
    } catch (err) {
      return { data: null, error: err.message };
    }
  },
  resetForReatribuicao: async (applicationId) => {
    try {
      const res = await fetchApi(`/applications/${applicationId}/reset_for_reatribuicao`, { method: 'POST' });
      return { data: res, error: null };
    } catch (err) {
      return { data: null, error: err.message };
    }
  },
  resetSpecialAttendanceForReatribuicao: async (applicationId) => {
    try {
      const res = await fetchApi(`/applications/${applicationId}/reset_special_attendance_for_reatribuicao`, {
        method: 'POST',
      });
      return { data: res, error: null };
    } catch (err) {
      return { data: null, error: err.message };
    }
  },
  bulkAssignExemption: async (applicationIds, analystId) => {
    try {
      const res = await fetchApi('/applications/bulk_assign_exemption', {
        method: 'POST',
        body: JSON.stringify({ application_ids: applicationIds, analyst_id: analystId })
      });
      return { data: res, error: null };
    } catch (err) {
      return { data: null, error: err.message };
    }
  },
  bulkAssignCurricular: async (applicationIds, analystId) => {
    try {
      const res = await fetchApi('/applications/bulk_assign_curricular', {
        method: 'POST',
        body: JSON.stringify({ application_ids: applicationIds, analyst_id: analystId })
      });
      return { data: res, error: null };
    } catch (err) {
      return { data: null, error: err.message };
    }
  },
  bulkAssignAppeals: async (appealIds, analystId) => {
    try {
      const res = await fetchApi('/appeals/bulk_assign', {
        method: 'POST',
        body: JSON.stringify({ appeal_ids: appealIds, analyst_id: analystId })
      });
      return { data: res, error: null };
    } catch (err) {
      return { data: null, error: err.message };
    }
  },
  bulkAssignExemptionEquitable: async (applicationIds, analystIds) => {
    try {
      const res = await fetchApi('/applications/bulk_assign_exemption_equitable', {
        method: 'POST',
        body: JSON.stringify({ application_ids: applicationIds, analyst_ids: analystIds })
      });
      return { data: res, error: null };
    } catch (err) {
      return { data: null, error: err.message };
    }
  },
  bulkAssignCurricularEquitable: async (applicationIds, analystIds) => {
    try {
      const res = await fetchApi('/applications/bulk_assign_curricular_equitable', {
        method: 'POST',
        body: JSON.stringify({ application_ids: applicationIds, analyst_ids: analystIds })
      });
      return { data: res, error: null };
    } catch (err) {
      return { data: null, error: err.message };
    }
  },
  bulkAssignAppealsEquitable: async (appealIds, analystIds) => {
    try {
      const res = await fetchApi('/appeals/bulk_assign_equitable', {
        method: 'POST',
        body: JSON.stringify({ appeal_ids: appealIds, analyst_ids: analystIds })
      });
      return { data: res, error: null };
    } catch (err) {
      return { data: null, error: err.message };
    }
  },
  bulkAssignSpecialAttendance: async (applicationIds, analystId) => {
    try {
      const res = await fetchApi('/applications/bulk_assign_special_attendance', {
        method: 'POST',
        body: JSON.stringify({ application_ids: applicationIds, analyst_id: analystId })
      });
      return { data: res, error: null };
    } catch (err) {
      return { data: null, error: err.message };
    }
  },
  bulkAssignSpecialAttendanceEquitable: async (applicationIds, analystIds) => {
    try {
      const res = await fetchApi('/applications/bulk_assign_special_attendance_equitable', {
        method: 'POST',
        body: JSON.stringify({ application_ids: applicationIds, analyst_ids: analystIds })
      });
      return { data: res, error: null };
    } catch (err) {
      return { data: null, error: err.message };
    }
  },
  deleteApplication: async (id) => {
    try {
      await fetchApi(`/applications/${id}`, { method: 'DELETE' });
      return { error: null };
    } catch (err) {
      return { error: err.message };
    }
  },
  excludeAndReapply: async (applicationId, data) => {
    try {
      const res = await fetchApi(`/applications/${applicationId}/exclude_and_reapply`, {
        method: 'POST',
        body: JSON.stringify(data)
      });
      return { data: res, error: null };
    } catch (err) {
      return { data: null, error: err.message };
    }
  },

  createCourse: async (data) => {
    try {
      const res = await fetchApi('/courses', { method: 'POST', body: JSON.stringify(data) });
      return { data: res, error: null };
    } catch (err) {
      return { data: null, error: err.message };
    }
  },
  updateCourse: async (id, data) => {
    try {
      const res = await fetchApi(`/courses/${id}`, { method: 'PUT', body: JSON.stringify(data) });
      return { data: res, error: null };
    } catch (err) {
      return { data: null, error: err.message };
    }
  },
  deleteCourse: async (id) => {
    try {
      await fetchApi(`/courses/${id}`, { method: 'DELETE' });
      return { error: null };
    } catch (err) {
      return { error: err.message };
    }
  },

  // Processos - CRUD (compatível com formato { data, error })
  createProcess: async (processData) => {
    try {
      const data = await fetchApi('/processes', {
        method: 'POST',
        body: JSON.stringify(processData),
      });
      return { data, error: null };
    } catch (err) {
      return { data: null, error: err.message };
    }
  },
  updateProcess: async (id, processData) => {
    try {
      const data = await fetchApi(`/processes/${id}`, {
        method: 'PUT',
        body: JSON.stringify(processData),
      });
      return { data, error: null };
    } catch (err) {
      return { data: null, error: err.message };
    }
  },
  deleteProcess: async (id) => {
    try {
      await fetchApi(`/processes/${id}`, { method: 'DELETE' });
      return { error: null };
    } catch (err) {
      return { error: err.message };
    }
  },

  publishProcessAnswerKey: async (processId, body) => {
    try {
      const data = await fetchApi(`/processes/${processId}/answer_key/publish`, {
        method: 'POST',
        body: JSON.stringify(body || {}),
      });
      return { data, error: null };
    } catch (err) {
      return { data: null, error: err.message };
    }
  },
  getProcessAnswerKeyPublic: (processId) =>
    fetchApi(`/processes/${processId}/answer_key/public`),
  getProcessAnswerKeyHistory: (processId) =>
    fetchApi(`/processes/${processId}/answer_key/history`),

  getExamVenues: (processId) => fetchApi(`/processes/${processId}/exam_venues`),
  createExamVenue: (processId, data) =>
    fetchApi(`/processes/${processId}/exam_venues`, { method: 'POST', body: JSON.stringify(data) }),
  updateExamVenue: (venueId, data) =>
    fetchApi(`/exam_venues/${venueId}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteExamVenue: (venueId) => fetchApi(`/exam_venues/${venueId}`, { method: 'DELETE' }),
  createExamRoom: (venueId, data) =>
    fetchApi(`/exam_venues/${venueId}/rooms`, { method: 'POST', body: JSON.stringify(data) }),
  updateExamRoom: (roomId, data) => fetchApi(`/exam_rooms/${roomId}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteExamRoom: (roomId) => fetchApi(`/exam_rooms/${roomId}`, { method: 'DELETE' }),
  getExamSheetsDistributionPreview: (processId) =>
    fetchApi(`/processes/${processId}/exam_sheets/distribution_preview`),
  exportExamSheetsPendingCsv: async (processId) => {
    const url = `${typeof window !== 'undefined' ? window.location.origin : ''}${API_BASE}/processes/${processId}/exam_sheets/pending_export`;
    const res = await fetch(url, { method: 'GET', credentials: 'include', cache: 'no-store' });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || 'Erro ao exportar pendentes');
    }
    const blob = await res.blob();
    const cd = res.headers.get('Content-Disposition') || '';
    const filename = cd.match(/filename[*]?=(?:UTF-8'')?["]?([^";\n]+)/i)?.[1]?.trim() || `pendentes_alocacao_processo_${processId}.csv`;
    return { blob, filename };
  },
  exportProcessExamSheetsZip: async (processId, opts = {}) => {
    const params = new URLSearchParams();
    if (opts.mode) params.set('mode', String(opts.mode));
    if (opts.venueId != null && opts.venueId !== '') params.set('venue_id', String(opts.venueId));
    if (opts.roomId != null && opts.roomId !== '') params.set('room_id', String(opts.roomId));
    const q = params.toString() ? `?${params.toString()}` : '';
    const url = `${typeof window !== 'undefined' ? window.location.origin : ''}${API_BASE}/processes/${processId}/exam_sheets/export${q}`;
    const res = await fetch(url, { method: 'GET', credentials: 'include', cache: 'no-store' });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || 'Erro ao gerar folhas');
    }
    const blob = await res.blob();
    let filename = res.headers.get('X-Export-Filename');
    if (!filename) {
      const cd = res.headers.get('Content-Disposition') || '';
      filename = cd.match(/filename[*]?=(?:UTF-8'')?["]?([^";\n]+)/i)?.[1]?.trim() || `folhas_prova_processo_${processId}.zip`;
    }
    return { blob, filename };
  },

  getSpecialAttendanceTypes: (opts = {}) => {
    const params = new URLSearchParams();
    if (opts.activeOnly) params.set('active_only', '1');
    if (opts.processId != null && opts.processId !== '') params.set('process_id', String(opts.processId));
    const q = params.toString() ? `?${params.toString()}` : '';
    return fetchApi(`/special_attendance_types${q}`);
  },
  createSpecialAttendanceType: (data) =>
    fetchApi('/special_attendance_types', { method: 'POST', body: JSON.stringify(data) }),
  updateSpecialAttendanceType: (id, data) =>
    fetchApi(`/special_attendance_types/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  getSpecialAttendanceRequests: (processId) => {
    const q = processId ? `?process_id=${encodeURIComponent(processId)}` : '';
    return fetchApi(`/special_attendance_requests${q}`);
  },
  updateSpecialAttendanceRequest: (id, data) =>
    fetchApi(`/special_attendance_requests/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  saveSpecialAttendanceApplication: async (applicationId, data) => {
    try {
      const res = await fetchApi(`/applications/${applicationId}/special_attendance`, {
        method: 'PUT',
        body: JSON.stringify(data),
      });
      return { data: res, error: null };
    } catch (err) {
      return { data: null, error: err.message };
    }
  },

  getPayments: () => fetchApi('/payments'),
  createPayment: async (data) => {
    try {
      const res = await fetchApi('/payments', { method: 'POST', body: JSON.stringify(data) });
      return { data: res, error: null };
    } catch (err) {
      return { data: null, error: err.message };
    }
  },
  updatePayment: async (id, data) => {
    try {
      const res = await fetchApi(`/payments/${id}`, { method: 'PUT', body: JSON.stringify(data) });
      return { data: res, error: null };
    } catch (err) {
      return { data: null, error: err.message };
    }
  },
  generateGruUrl: async (applicationId) => {
    try {
      const res = await fetchApi('/payments/generate-gru', {
        method: 'POST',
        body: JSON.stringify({ application_id: applicationId })
      });
      return { data: res, error: null };
    } catch (err) {
      return { data: null, error: err.message };
    }
  },

  getUsers: (role) => fetchApi(`/users${role ? `?role=${role}` : ''}`),
  createUser: async (data) => {
    try {
      const res = await fetchApi('/users', { method: 'POST', body: JSON.stringify(data) });
      return { data: res, error: null };
    } catch (err) {
      return { data: null, error: err.message };
    }
  },
  updateUser: async (id, data) => {
    try {
      const res = await fetchApi(`/users/${id}`, { method: 'PUT', body: JSON.stringify(data) });
      return { data: res, error: null };
    } catch (err) {
      return { data: null, error: err.message };
    }
  },
  deleteUser: async (id) => {
    try {
      await fetchApi(`/users/${id}`, { method: 'DELETE' });
      return { error: null };
    } catch (err) {
      return { error: err.message };
    }
  },

  getQuestionnaires: (all = false) => fetchApi(`/questionnaires${all ? '?all=1' : ''}`),
  getQuestionnaire: (id) => fetchApi(`/questionnaires/${id}`),
  createQuestionnaire: async (data) => {
    try {
      const res = await fetchApi('/questionnaires', { method: 'POST', body: JSON.stringify(data) });
      return { data: res, error: null };
    } catch (err) {
      return { data: null, error: err.message };
    }
  },
  updateQuestionnaire: async (id, data) => {
    try {
      const res = await fetchApi(`/questionnaires/${id}`, { method: 'PUT', body: JSON.stringify(data) });
      return { data: res, error: null };
    } catch (err) {
      return { data: null, error: err.message };
    }
  },
  deleteQuestionnaire: async (id) => {
    try {
      await fetchApi(`/questionnaires/${id}`, { method: 'DELETE' });
      return { error: null };
    } catch (err) {
      return { error: err.message };
    }
  },
  getQuotas: (all = false) => fetchApi(`/quotas${all ? '?all=1' : ''}`),
  getProcessQuotas: (processId) => fetchApi(`/processes/${processId}/quotas`),
  saveProcessQuotas: (processId, quotaIds) => fetchApi(`/processes/${processId}/quotas`, { method: 'PUT', body: JSON.stringify({ quota_ids: quotaIds }) }),
  getProcessPrecedence: (processId) => fetchApi(`/processes/${processId}/precedence`),
  saveProcessPrecedence: (processId, rules) => fetchApi(`/processes/${processId}/precedence`, { method: 'PUT', body: JSON.stringify({ rules }) }),
  getProcessReallocation: (processId) => fetchApi(`/processes/${processId}/reallocation`),
  saveProcessReallocation: (processId, rules) => fetchApi(`/processes/${processId}/reallocation`, { method: 'PUT', body: JSON.stringify({ rules }) }),
  getClassification: (processId, courseId, listType = 'preliminary') =>
    fetchApi(`/processes/${processId}/courses/${courseId}/classification?list_type=${listType}`),
  createQuota: async (data) => {
    try {
      const res = await fetchApi('/quotas', { method: 'POST', body: JSON.stringify(data) });
      return { data: res, error: null };
    } catch (err) {
      return { data: null, error: err.message };
    }
  },
  updateQuota: async (id, data) => {
    try {
      const res = await fetchApi(`/quotas/${id}`, { method: 'PUT', body: JSON.stringify(data) });
      return { data: res, error: null };
    } catch (err) {
      return { data: null, error: err.message };
    }
  },
  deleteQuota: async (id) => {
    try {
      await fetchApi(`/quotas/${id}`, { method: 'DELETE' });
      return { error: null };
    } catch (err) {
      return { error: err.message };
    }
  },
  getCourseTypes: (all = false) => fetchApi(`/course_types${all ? '?all=1' : ''}`),
  createCourseType: async (data) => {
    try {
      const res = await fetchApi('/course_types', { method: 'POST', body: JSON.stringify(data) });
      return { data: res, error: null };
    } catch (err) {
      return { data: null, error: err.message };
    }
  },
  updateCourseType: async (id, data) => {
    try {
      const res = await fetchApi(`/course_types/${id}`, { method: 'PUT', body: JSON.stringify(data) });
      return { data: res, error: null };
    } catch (err) {
      return { data: null, error: err.message };
    }
  },

  getPaymentMethods: () => fetchApi('/payment_methods'),
  createPaymentMethod: async (data) => {
    try {
      const res = await fetchApi('/payment_methods', { method: 'POST', body: JSON.stringify(data) });
      return { data: res, error: null };
    } catch (err) {
      return { data: null, error: err.message };
    }
  },
  updatePaymentMethod: async (id, data) => {
    try {
      const res = await fetchApi(`/payment_methods/${id}`, { method: 'PUT', body: JSON.stringify(data) });
      return { data: res, error: null };
    } catch (err) {
      return { data: null, error: err.message };
    }
  },
  deletePaymentMethod: async (id) => {
    try {
      await fetchApi(`/payment_methods/${id}`, { method: 'DELETE' });
      return { error: null };
    } catch (err) {
      return { error: err.message };
    }
  },

  getTiebreakerCriteria: (all = false) => fetchApi(`/tiebreaker_criteria${all ? '?all=1' : ''}`),
  createTiebreakerCriterion: async (data) => {
    try {
      const res = await fetchApi('/tiebreaker_criteria', { method: 'POST', body: JSON.stringify(data) });
      return { data: res, error: null };
    } catch (err) {
      return { data: null, error: err.message };
    }
  },
  updateTiebreakerCriterion: async (id, data) => {
    try {
      const res = await fetchApi(`/tiebreaker_criteria/${id}`, { method: 'PUT', body: JSON.stringify(data) });
      return { data: res, error: null };
    } catch (err) {
      return { data: null, error: err.message };
    }
  },
  deleteTiebreakerCriterion: async (id) => {
    try {
      await fetchApi(`/tiebreaker_criteria/${id}`, { method: 'DELETE' });
      return { error: null };
    } catch (err) {
      return { error: err.message };
    }
  },

  getRejectionReasons: (opts = false) => {
    const all = typeof opts === 'boolean' ? opts : Boolean(opts?.all);
    const context = typeof opts === 'object' && opts?.context ? String(opts.context) : '';
    const params = new URLSearchParams();
    if (all) params.set('all', '1');
    if (context) params.set('context', context);
    const qs = params.toString();
    return fetchApi(`/rejection_reasons${qs ? `?${qs}` : ''}`);
  },
  getEssayEliminationReasons: (all = false) => fetchApi(`/essay_elimination_reasons${all ? '?all=1' : ''}`),
  createEssayEliminationReason: async (data) => {
    try {
      const res = await fetchApi('/essay_elimination_reasons', { method: 'POST', body: JSON.stringify(data) });
      return { data: res, error: null };
    } catch (err) {
      return { data: null, error: err.message };
    }
  },
  updateEssayEliminationReason: async (id, data) => {
    try {
      const res = await fetchApi(`/essay_elimination_reasons/${id}`, { method: 'PUT', body: JSON.stringify(data) });
      return { data: res, error: null };
    } catch (err) {
      return { data: null, error: err.message };
    }
  },
  deleteEssayEliminationReason: async (id) => {
    try {
      await fetchApi(`/essay_elimination_reasons/${id}`, { method: 'DELETE' });
      return { error: null };
    } catch (err) {
      return { error: err.message };
    }
  },
  recalculateEssayEligibility: async (processId) => {
    try {
      const res = await fetchApi(`/processes/${processId}/recalculate_essay_eligibility`, {
        method: 'POST',
        body: JSON.stringify({}),
      });
      return { data: res, error: null };
    } catch (err) {
      return { data: null, error: err.message };
    }
  },
  createRejectionReason: async (data) => {
    try {
      const res = await fetchApi('/rejection_reasons', { method: 'POST', body: JSON.stringify(data) });
      return { data: res, error: null };
    } catch (err) {
      return { data: null, error: err.message };
    }
  },
  updateRejectionReason: async (id, data) => {
    try {
      const res = await fetchApi(`/rejection_reasons/${id}`, { method: 'PUT', body: JSON.stringify(data) });
      return { data: res, error: null };
    } catch (err) {
      return { data: null, error: err.message };
    }
  },
  deleteRejectionReason: async (id) => {
    try {
      await fetchApi(`/rejection_reasons/${id}`, { method: 'DELETE' });
      return { error: null };
    } catch (err) {
      return { error: err.message };
    }
  },

  getExemptionRequests: (opts) => {
    const params = new URLSearchParams();
    if (opts?.processId) params.set('process_id', opts.processId);
    return fetchApi(`/exemption_requests${params.toString() ? '?' + params.toString() : ''}`);
  },
  createExemptionRequest: async (data) => {
    try {
      const res = await fetchApi('/exemption_requests', { method: 'POST', body: JSON.stringify(data) });
      return { data: res, error: null };
    } catch (err) {
      return { data: null, error: err.message };
    }
  },
  updateExemptionRequest: async (id, data) => {
    try {
      const res = await fetchApi(`/exemption_requests/${id}`, { method: 'PUT', body: JSON.stringify(data) });
      return { data: res, error: null };
    } catch (err) {
      return { data: null, error: err.message };
    }
  },

  getCourseChanges: () => fetchApi('/course_changes'),
  getQuotaChanges: () => fetchApi('/quota_changes'),
  createCourseChange: async (data) => {
    try {
      const res = await fetchApi('/course_changes', { method: 'POST', body: JSON.stringify(data) });
      return { data: res, error: null };
    } catch (err) {
      return { data: null, error: err.message };
    }
  },

  getStatusChanges: () => fetchApi('/status_changes'),
  createStatusChange: async (data) => {
    try {
      const res = await fetchApi('/status_changes', { method: 'POST', body: JSON.stringify(data) });
      return { data: res, error: null };
    } catch (err) {
      return { data: null, error: err.message };
    }
  },

  register: async (data) => {
    try {
      const res = await fetchApi('/register', { method: 'POST', body: JSON.stringify(data) });
      return { data: res, error: null };
    } catch (err) {
      return { data: null, error: err.message };
    }
  },

  registerStart: async (email, emailConfirm) => {
    try {
      const res = await fetchApi('/register/start', {
        method: 'POST',
        body: JSON.stringify({ email, email_confirm: emailConfirm }),
      });
      return { data: res, error: null };
    } catch (err) {
      return { data: null, error: err.message };
    }
  },

  registerResend: async (email) => {
    try {
      const res = await fetchApi('/register/resend', {
        method: 'POST',
        body: JSON.stringify({ email }),
      });
      return { data: res, error: null };
    } catch (err) {
      return { data: null, error: err.message };
    }
  },

  registerVerifyToken: async (token) => {
    try {
      const res = await fetchApi(`/register/verify-token?token=${encodeURIComponent(token)}`);
      return { data: res, error: null };
    } catch (err) {
      return { data: null, error: err.message };
    }
  },

  registerComplete: async (token, data) => {
    try {
      const res = await fetchApi('/register/complete', {
        method: 'POST',
        body: JSON.stringify({ token, ...data }),
      });
      return { data: res, error: null };
    } catch (err) {
      return { data: null, error: err.message };
    }
  },

  getAnalyses: () => fetchApi('/analyses'),
  createAnalysis: async (data) => {
    try {
      const res = await fetchApi('/analyses', { method: 'POST', body: JSON.stringify(data) });
      return { data: res, error: null };
    } catch (err) {
      return { data: null, error: err.message };
    }
  },

  getAppeals: (opts) => {
    const params = new URLSearchParams();
    if (opts?.processId) params.set('process_id', opts.processId);
    return fetchApi(`/appeals${params.toString() ? '?' + params.toString() : ''}`);
  },
  createAppeal: async (data) => {
    try {
      const res = await fetchApi('/appeals', { method: 'POST', body: JSON.stringify(data) });
      return { data: res, error: null };
    } catch (err) {
      return { data: null, error: err.message };
    }
  },
  getAnswerKeyAppealQuestionHint: async (applicationId, questionNumber) => {
    try {
      const res = await fetchApi(
        `/applications/${applicationId}/answer-key-appeal-question?question_number=${encodeURIComponent(questionNumber)}`
      );
      return { data: res, error: null };
    } catch (err) {
      return { data: null, error: err.message };
    }
  },
  getAppealAnswerKeyQuestionHint: async (appealId) => {
    try {
      const res = await fetchApi(`/appeals/${appealId}/answer-key-question-hint`);
      return { data: res, error: null };
    } catch (err) {
      return { data: null, error: err.message };
    }
  },
  downloadEnrollmentCard: async (applicationId) => {
    const url = `${API_BASE}/applications/${applicationId}/enrollment-card?_=${Date.now()}`;
    const res = await fetch(url, { credentials: 'include', cache: 'no-store' });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || 'Erro ao gerar cartão de inscrição');
    }
    const blob = await res.blob();
    let filename = `cartao_inscricao_${applicationId}.pdf`;
    const cd = res.headers.get('Content-Disposition') || '';
    const m = cd.match(/filename\*=UTF-8''([^;\n]+)/i) || cd.match(/filename="?([^";\n]+)/i);
    if (m && m[1]) {
      try {
        filename = decodeURIComponent(m[1].trim());
      } catch {
        filename = m[1].trim();
      }
    }
    return { blob, filename };
  },
  downloadRegistrationReceipt: async (applicationId) => {
    const url = `${API_BASE}/applications/${applicationId}/registration-receipt?_=${Date.now()}`;
    const res = await fetch(url, { credentials: 'include', cache: 'no-store' });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || 'Erro ao gerar comprovante de inscrição');
    }
    const blob = await res.blob();
    let filename = `comprovante_inscricao_${applicationId}.pdf`;
    const cd = res.headers.get('Content-Disposition') || '';
    const m = cd.match(/filename\*=UTF-8''([^;\n]+)/i) || cd.match(/filename="?([^";\n]+)/i);
    if (m && m[1]) {
      try {
        filename = decodeURIComponent(m[1].trim());
      } catch {
        filename = m[1].trim();
      }
    }
    return { blob, filename };
  },
  getOpenEditalChallengeProcesses: async () => {
    try {
      const res = await fetchApi('/public/edital-challenges/open-processes');
      return { data: res, error: null };
    } catch (err) {
      return { data: null, error: err.message };
    }
  },
  getPublicEditalChallengeProcessDocuments: async (processId) => {
    try {
      const res = await fetchApi(`/public/edital-challenges/processes/${processId}/documents`);
      return { data: res, error: null };
    } catch (err) {
      return { data: null, error: err.message };
    }
  },
  downloadPublicEditalChallengeProcessDocument: async (docId) => {
    const url = `${API_BASE}/public/edital-challenges/process-documents/${docId}/download?_=${Date.now()}`;
    const res = await fetch(url, { credentials: 'include', cache: 'no-store' });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || 'Erro ao baixar edital/anexo');
    }
    const blob = await res.blob();
    let filename = `edital_${docId}.pdf`;
    const cd = res.headers.get('Content-Disposition') || '';
    const m = cd.match(/filename\*=UTF-8''([^;\n]+)/i) || cd.match(/filename="?([^";\n]+)/i);
    if (m && m[1]) {
      try {
        filename = decodeURIComponent(m[1].trim());
      } catch {
        filename = m[1].trim();
      }
    }
    return { blob, filename };
  },
  uploadEditalChallengeDocument: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const res = await fetch(`${API_BASE}/public/edital-challenges/upload`, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || 'Erro no upload');
    return data;
  },
  submitEditalChallenge: async (payload) => {
    try {
      const res = await fetchApi('/public/edital-challenges', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      return { data: res, error: null };
    } catch (err) {
      return { data: null, error: err.message };
    }
  },
  getEditalChallengeByProtocol: async (protocol) => {
    try {
      const res = await fetchApi(`/public/edital-challenges/by-protocol/${encodeURIComponent(protocol)}`);
      return { data: res, error: null };
    } catch (err) {
      return { data: null, error: err.message };
    }
  },
  downloadEditalChallengeReceipt: async (protocol) => {
    const url = `${API_BASE}/public/edital-challenges/by-protocol/${encodeURIComponent(protocol)}/receipt?_=${Date.now()}`;
    const res = await fetch(url, { credentials: 'include', cache: 'no-store' });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || 'Erro ao gerar comprovante da impugnação');
    }
    const blob = await res.blob();
    let filename = `impugnacao_${protocol}.pdf`;
    const cd = res.headers.get('Content-Disposition') || '';
    const m = cd.match(/filename\*=UTF-8''([^;\n]+)/i) || cd.match(/filename="?([^";\n]+)/i);
    if (m && m[1]) {
      try {
        filename = decodeURIComponent(m[1].trim());
      } catch {
        filename = m[1].trim();
      }
    }
    return { blob, filename };
  },
  listEditalChallenges: async (params = {}) => {
    try {
      const qs = new URLSearchParams();
      if (params.process_id) qs.set('process_id', params.process_id);
      if (params.status) qs.set('status', params.status);
      const q = qs.toString();
      const res = await fetchApi(`/edital-challenges${q ? `?${q}` : ''}`);
      return { data: res, error: null };
    } catch (err) {
      return { data: null, error: err.message };
    }
  },
  respondEditalChallenge: async (id, data) => {
    try {
      const res = await fetchApi(`/edital-challenges/${id}/respond`, {
        method: 'PUT',
        body: JSON.stringify(data),
      });
      return { data: res, error: null };
    } catch (err) {
      return { data: null, error: err.message };
    }
  },
  updateAppeal: async (id, data) => {
    try {
      const res = await fetchApi(`/appeals/${id}`, { method: 'PUT', body: JSON.stringify(data) });
      return { data: res, error: null };
    } catch (err) {
      return { data: null, error: err.message };
    }
  },
  acceptAppealAssignment: async (appealId) => {
    try {
      const res = await fetchApi(`/appeals/${appealId}/accept`, { method: 'POST' });
      return { data: res, error: null };
    } catch (err) {
      return { data: null, error: err.message };
    }
  },
  returnAppealAssignment: async (appealId, reason) => {
    try {
      const res = await fetchApi(`/appeals/${appealId}/return_assignment`, {
        method: 'POST',
        body: JSON.stringify({ reason: reason || '' }),
      });
      return { data: res, error: null };
    } catch (err) {
      return { data: null, error: err.message };
    }
  },
  publishAppealResponseToCandidate: async (appealId) => {
    try {
      const res = await fetchApi(`/appeals/${appealId}/publish_response_to_candidate`, { method: 'POST', body: '{}' });
      return { data: res, error: null };
    } catch (err) {
      return { data: null, error: err.message };
    }
  },
  bulkPublishAppealResponses: async (processId) => {
    try {
      const res = await fetchApi('/appeals/bulk_publish_response', {
        method: 'POST',
        body: JSON.stringify({ process_id: processId }),
      });
      return { data: res, error: null };
    } catch (err) {
      return { data: null, error: err.message };
    }
  },

  getReportHistoryChanges: (processId, search) => {
    let url = `/reports/history_changes?process_id=${processId}`;
    if (search && String(search).trim()) url += `&search=${encodeURIComponent(String(search).trim())}`;
    return fetchApi(url);
  },
  getReportCandidatesFullContact: (processId, search) => {
    let url = `/reports/candidates_full_contact?process_id=${processId}`;
    if (search && String(search).trim()) url += `&search=${encodeURIComponent(String(search).trim())}`;
    return fetchApi(url);
  },
  getReportExemptionBeforeAppeal: (processId) => fetchApi(`/reports/exemption_before_appeal?process_id=${processId}`),
  getReportExemptionAfterAppeal: (processId) => fetchApi(`/reports/exemption_after_appeal?process_id=${processId}`),
  getReportSpecialAttendanceBeforeAppeal: (processId) =>
    fetchApi(`/reports/special_attendance_before_appeal?process_id=${processId}`),
  getReportSpecialAttendanceAfterAppeal: (processId) =>
    fetchApi(`/reports/special_attendance_after_appeal?process_id=${processId}`),
  getReportProvaPreliminaryEnrolled: (processId) =>
    fetchApi(`/reports/prova_preliminary_enrolled?process_id=${processId}`),
  getReportProvaHomologationEnrollments: (processId) =>
    fetchApi(`/reports/prova_homologation_enrollments?process_id=${processId}`),
  getReportProvaHomologatedExamPlaces: (processId) =>
    fetchApi(`/reports/prova_homologated_exam_places?process_id=${processId}`),
  getReportProvaHomologatedExamByRoom: (processId) =>
    fetchApi(`/reports/prova_homologated_exam_by_room?process_id=${processId}`),
  getReportProvaPresenceByRoom: (processId) =>
    fetchApi(`/reports/prova_presence_by_room?process_id=${processId}`),
  getReportProvaGabarito: (processId, phase) =>
    fetchApi(`/reports/prova_gabarito?process_id=${processId}&phase=${encodeURIComponent(phase || 'preliminary')}`),
  /** URL direta do PDF (Content-Disposition com nome padronizado). */
  getReportProvaGabaritoPdfUrl: (processId, phase) => {
    const params = new URLSearchParams({
      process_id: String(processId),
      phase: phase || 'preliminary',
      format: 'pdf',
      _: String(Date.now()),
    });
    return `/api/reports/prova_gabarito?${params.toString()}`;
  },
  getReportPreliminaryList: (processId) => fetchApi(`/reports/preliminary_list?process_id=${processId}`),
  getReportFinalApprovedList: (processId) => fetchApi(`/reports/final_approved_list?process_id=${processId}`),
  getReportClassificationPreliminary: (processId) => fetchApi(`/reports/classification_preliminary?process_id=${processId}`),
  getReportClassificationFinal: (processId) => fetchApi(`/reports/classification_final?process_id=${processId}`),
  getReportProvaResultadoPreliminar: (processId) =>
    fetchApi(`/reports/prova_resultado_preliminar?process_id=${processId}`),
  getReportProvaResultadoFinal: (processId) => fetchApi(`/reports/prova_resultado_final?process_id=${processId}`),
  getReportHomologationSignatures: (processId) => fetchApi(`/reports/homologation_signatures?process_id=${processId}`),
  getReportEnrollmentData: (processId, format) => fetchApi(`/reports/enrollment_data?process_id=${processId}&format=${format || 'resumo'}`),
  getReportWaitingListByQuota: (processId) => fetchApi(`/reports/waiting_list_by_quota?process_id=${processId}`),
  getReportQuestionnaireSocioeconomic: (processId, opts = {}) => {
    const params = new URLSearchParams({ process_id: processId });
    if (opts.scope) params.set('scope', opts.scope);
    if (opts.format) params.set('format', opts.format);
    if (opts.identify !== undefined) params.set('identify', opts.identify ? '1' : '0');
    if (opts.search) params.set('search', opts.search);
    return fetchApi(`/reports/questionnaire_socioeconomic?${params}`);
  },
};
